import * as React from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout, SettingsNav } from "@bi-studio/shared/components";
import { useAuth } from "@bi-studio/shared/hooks";
import { changePassword } from "@bi-studio/shared/apis";
import { Card, Title, Text, Stack, PasswordInput, Button, Group, Alert, Container, useMantineColorScheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { AiAssistant } from "@/components/ai";

export default function SettingsPasswordPage() {
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const { colorScheme } = useMantineColorScheme();

    const form = useForm({
        initialValues: {
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
        validate: {
            currentPassword: (value) => (value.length < 1 ? "Current password is required" : null),
            newPassword: (value) => (value.length < 6 ? "Password must be at least 6 characters" : null),
            confirmPassword: (value, values) =>
                value !== values.newPassword ? "Passwords do not match" : null,
        },
    });

    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    React.useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate("/login");
        }
    }, [isAuthenticated, authLoading, navigate]);

    const handleSubmit = async (values: typeof form.values) => {
        if (!token) return;
        setIsLoading(true);
        setError(null);
        try {
            await changePassword(token, values.currentPassword, values.newPassword);
            notifications.show({
                title: 'Success',
                message: 'Password updated successfully',
                color: 'green',
            });
            form.reset();
        } catch (err: any) {
            setError(err.message || "Failed to update password");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isAuthenticated && !authLoading) {
        return null;
    }

    const sidebarContent = <SettingsNav />;

    return (
        <AppLayout
            showSidebar
            sidebarContent={sidebarContent}
            onLogout={handleLogout}
            title="Settings"
            subtitle="Security & Password"
            floatingContent={<AiAssistant />}
        >
            <Container size="xl" p={0}>
                <Stack gap="xl">
                    <div>
                        <Title order={2} fw={700}>Security Settings</Title>
                        <Text c="dimmed">Update your password to keep your account secure.</Text>
                    </div>

                    <div className="max-w-2xl">
                        <Card 
                            withBorder 
                            radius="md" 
                            p="xl"
                            style={{
                                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'var(--mantine-color-white)',
                                borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined
                            }}
                        >
                            <Stack gap="md">
                                <Title order={4}>Change Password</Title>

                                {error && (
                                    <Alert color="red" title="Error" radius="md">
                                        {error}
                                    </Alert>
                                )}

                                <form onSubmit={form.onSubmit(handleSubmit)}>
                                    <Stack gap="md">
                                        <PasswordInput
                                            label="Current Password"
                                            placeholder="Enter current password"
                                            required
                                            {...form.getInputProps("currentPassword")}
                                            radius="md"
                                        />
                                        <PasswordInput
                                            label="New Password"
                                            placeholder="Enter new password"
                                            required
                                            {...form.getInputProps("newPassword")}
                                            radius="md"
                                        />
                                        <PasswordInput
                                            label="Confirm New Password"
                                            placeholder="Confirm new password"
                                            required
                                            {...form.getInputProps("confirmPassword")}
                                            radius="md"
                                        />

                                        <Group justify="flex-end" mt="md">
                                            <Button 
                                                type="submit" 
                                                loading={isLoading} 
                                                radius="md"
                                                color="black"
                                                c="white"
                                            >
                                                Update Password
                                            </Button>
                                        </Group>
                                    </Stack>
                                </form>
                            </Stack>
                        </Card>
                    </div>
                </Stack>
            </Container>
        </AppLayout>
    );
}
